import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';
import { RadioButton } from 'primereact/radiobutton';
import Draggable from 'react-draggable';

interface Step3PositionsProps {
    documentRecipients: any[];
    signaturePositions: any[];
    selectedSigner: any;
    setSelectedSigner: (signer: any) => void;
    isViewMode?: boolean;
    canvasLoading: boolean;
    pageCanvases: any[];
    canvasRefs: React.MutableRefObject<any[]>;
    draggableRefs: React.MutableRefObject<Record<string, React.RefObject<any>>>;
    handleDrop: (event: React.DragEvent, pageIndex: number) => void;
    handleDragStop: (event: any, data: any, locationSeqId: string) => void;
    selectedSignatureType: string;
    setSelectedSignatureType: (type: string) => void;
    removeSignaturePosition: (id: string) => void;
    setSignaturePositions: (pos: any[]) => void;
    normalizeLocationSeq: (pos: any[]) => any[];
    isTemplateMode?: boolean;
    mainFiles: any[];
    activeMainFileIndex: number;
    onSetActiveFile: (index: number) => void;
    enableZoom?: boolean;
}

export const Step3Positions: React.FC<Step3PositionsProps> = ({
    documentRecipients,
    signaturePositions,
    selectedSigner,
    setSelectedSigner,
    isViewMode = false,
    canvasLoading,
    pageCanvases,
    canvasRefs,
    draggableRefs,
    handleDrop,
    handleDragStop,
    selectedSignatureType,
    setSelectedSignatureType,
    removeSignaturePosition,
    setSignaturePositions,
    normalizeLocationSeq,
    isTemplateMode = false,
    mainFiles = [],
    activeMainFileIndex = 0,
    onSetActiveFile,
    enableZoom = false
}) => {
    const activeFile = mainFiles[activeMainFileIndex];
    const [pageScales, setPageScales] = React.useState<Record<number, number>>({});
    const [activePageIndex, setActivePageIndex] = React.useState(0);
    const [zoomPercent, setZoomPercent] = React.useState(100);
    const pageWrapperRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
    const pageIntersectionRatios = React.useRef<Record<number, number>>({});
    const signatureTypeOptions = [
        { label: 'Ký số', value: 'AgtDigitalVerified', icon: 'pi pi-verified' },
        { label: 'Ký ảnh', value: 'AgtImageVerified', icon: 'pi pi-image' }
    ];
    const changeZoom = (amount: number) => {
        setZoomPercent(currentZoom => Math.min(200, Math.max(50, currentZoom + amount)));
    };
    const getEffectiveSignatureType = (position: any) => {
        const rawType = position?.signatureTypeEnumId;
        return rawType === 'AgtImageVerified' || rawType === 'AgtHandwritingVerified'
            ? 'AgtImageVerified'
            : 'AgtDigitalVerified';
    };

    const updatePageScales = React.useCallback(() => {
        setPageScales(prev => {
            const next: Record<number, number> = {};
            let changed = Object.keys(prev).length !== pageCanvases.length;

            pageCanvases.forEach((canvas, index) => {
                const renderedCanvas = canvasRefs.current[index];
                const baseWidth = Number(canvas?.width || renderedCanvas?.width || 0);
                const displayWidth =
                    renderedCanvas?.getBoundingClientRect().width ||
                    pageWrapperRefs.current[index]?.getBoundingClientRect().width ||
                    baseWidth;
                const scale = baseWidth > 0 && displayWidth > 0 ? displayWidth / baseWidth : 1;

                next[index] = scale;
                if (Math.abs((prev[index] || 1) - scale) > 0.001) {
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [canvasRefs, pageCanvases]);

    React.useEffect(() => {
        updatePageScales();

        const targets = pageCanvases
            .map((_, index) => canvasRefs.current[index] || pageWrapperRefs.current[index])
            .filter(Boolean) as Element[];
        let resizeObserver: ResizeObserver | null = null;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updatePageScales);
            targets.forEach(target => resizeObserver?.observe(target));
        }

        window.addEventListener('resize', updatePageScales);
        window.visualViewport?.addEventListener('resize', updatePageScales);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updatePageScales);
            window.visualViewport?.removeEventListener('resize', updatePageScales);
        };
    }, [activeMainFileIndex, canvasLoading, canvasRefs, pageCanvases, updatePageScales]);

    React.useEffect(() => {
        const animationFrame = window.requestAnimationFrame(updatePageScales);
        return () => window.cancelAnimationFrame(animationFrame);
    }, [zoomPercent, updatePageScales]);

    React.useEffect(() => {
        setZoomPercent(100);
    }, [activeMainFileIndex]);

    React.useEffect(() => {
        setActivePageIndex(0);
        pageIntersectionRatios.current = {};
    }, [activeMainFileIndex, pageCanvases.length]);

    React.useEffect(() => {
        if (canvasLoading || pageCanvases.length === 0 || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const pageIndex = Number((entry.target as HTMLElement).dataset.pageIndex);
                if (!Number.isNaN(pageIndex)) {
                    pageIntersectionRatios.current[pageIndex] = entry.isIntersecting ? entry.intersectionRatio : 0;
                }
            });

            const mostVisiblePage = Object.entries(pageIntersectionRatios.current)
                .sort(([, firstRatio], [, secondRatio]) => secondRatio - firstRatio)[0];
            if (mostVisiblePage && mostVisiblePage[1] > 0) {
                setActivePageIndex(Number(mostVisiblePage[0]));
            }
        }, {
            threshold: [0.1, 0.25, 0.5, 0.75]
        });

        pageCanvases.forEach((_, index) => {
            const pageElement = pageWrapperRefs.current[index];
            if (pageElement) observer.observe(pageElement);
        });

        return () => observer.disconnect();
    }, [activeMainFileIndex, canvasLoading, pageCanvases]);

    const isPositionForActiveFile = (position: any) => (
        position.agreementContentId && activeFile?.agreementContentId
            ? position.agreementContentId === activeFile.agreementContentId
            : (!position.fileId || position.fileId === activeFile?.fileId)
    );

    const getRecipientId = (recipient: any) => recipient?.party?.partyId || recipient?.party?.value || recipient?.partyId || '';

    const getRecipientLabel = (recipient: any, fallback?: string) => {
        const rawLabel = recipient?.party?.label || recipient?.partyName || fallback || 'Chưa gắn người ký';
        const match = String(rawLabel).match(/\]\s*-\s*(.+)$/);
        return match ? match[1] : rawLabel;
    };

    const getRecipientForPosition = (position: any) => {
        return documentRecipients.find(r => r._internalId && r._internalId === position.internalRecipientId)
            || documentRecipients.find(r => {
                const recipientId = getRecipientId(r);
                return recipientId && position.partyId && String(recipientId) === String(position.partyId);
            })
            || null;
    };

    const isPositionAssignedToRecipient = (position: any) => {
        const recipient = getRecipientForPosition(position);
        const recipientId = getRecipientId(recipient);
        return !!recipient && !!recipientId && !!position.partyId && String(recipientId) === String(position.partyId);
    };

    const getSignatureDisplayLabel = (position: any) => {
        const recipient = getRecipientForPosition(position);
        return getRecipientLabel(recipient, position.partyName || position.label);
    };

    const getRequiredSignatureHeight = (label: any, width: number, fontSize: number, signatureType: string, configuredHeight: number) => {
        if (signatureType === 'AgtImageVerified') return configuredHeight;

        const text = String(label || '');
        const usableWidth = Math.max(40, Number(width || 200) - 18);
        const avgCharWidth = Math.max(5, Number(fontSize || 12) * 0.55);
        const charsPerLine = Math.max(8, Math.floor(usableWidth / avgCharWidth));
        const signerLineCount = Math.max(1, Math.ceil(text.length / charsPerLine));
        const lineHeight = Math.ceil(Number(fontSize || 12) * 1.25);

        return Math.max(configuredHeight, (signerLineCount + 2) * lineHeight + 14);
    };

    const getEffectiveSignatureHeight = (position: any, width?: number, type?: string) => {
        const signatureType = type || getEffectiveSignatureType(position);
        const configuredHeight = Number(position.heightPx || (signatureType === 'AgtImageVerified' ? 100 : 65));
        return getRequiredSignatureHeight(
            getSignatureDisplayLabel(position),
            Number(width || position.widthPx || 200),
            Number(position.fontSize || 12),
            signatureType,
            configuredHeight
        );
    };

    const activeSignaturePositions = signaturePositions
        .filter(isPositionForActiveFile)
        .filter(isPositionAssignedToRecipient);

    const signatureCountByPage = signaturePositions
        .filter(isPositionForActiveFile)
        .reduce((counts: Record<number, number>, position) => {
            const pageIndex = Number(position.pageIndex);
            if (!Number.isNaN(pageIndex)) {
                counts[pageIndex] = (counts[pageIndex] || 0) + 1;
            }
            return counts;
        }, {});

    React.useEffect(() => {
        if (!signaturePositions.length) return;

        let changed = false;
        const nextPositions = signaturePositions.map(position => {
            const effectiveHeight = getEffectiveSignatureHeight(position);
            const currentHeight = Number(position.heightPx || 0);

            if (effectiveHeight > currentHeight) {
                changed = true;
                return { ...position, heightPx: effectiveHeight };
            }

            return position;
        });

        if (changed) {
            setSignaturePositions(normalizeLocationSeq(nextPositions));
        }
    }, [signaturePositions, documentRecipients, setSignaturePositions, normalizeLocationSeq]);

    const applyRecipientToPosition = (position: any, recipient: any) => {
        const recipientId = getRecipientId(recipient);
        const label = getRecipientLabel(recipient, position.label || position.partyName);
        return {
            ...position,
            internalRecipientId: recipient?._internalId || position.internalRecipientId,
            partyId: recipientId || position.partyId,
            partyName: recipient?.partyName || recipient?.party?.partyName || label,
            label,
            roleTypeId: recipient?.roleTypeId || position.roleTypeId,
            isUnmatchedTemplatePosition: false
        };
    };

    const getSelectedRecipient = () => {
        if (!selectedSigner) return null;
        return documentRecipients.find(r => r._internalId === selectedSigner._internalId)
            || documentRecipients.find(r => {
                const recipientId = getRecipientId(r);
                const selectedId = selectedSigner.partyId || selectedSigner.value;
                return recipientId && selectedId && String(recipientId) === String(selectedId);
            })
            || null;
    };

    const updatePositionById = (locationSeqId: string, updater: (position: any) => any) => {
        setSignaturePositions(normalizeLocationSeq(signaturePositions.map(item =>
            item.locationSeqId === locationSeqId ? updater(item) : item
        )));
    };

    const assignPositionToSelectedSigner = (position: any) => {
        const recipient = getSelectedRecipient();
        if (!recipient) return;
        updatePositionById(position.locationSeqId, item => applyRecipientToPosition(item, recipient));
    };

    const assignAllUnmatchedToSelectedSigner = () => {
        const recipient = getSelectedRecipient();
        if (!recipient) return;
        setSignaturePositions(normalizeLocationSeq(signaturePositions.map(item =>
            isPositionForActiveFile(item) && !isPositionAssignedToRecipient(item)
                ? applyRecipientToPosition(item, recipient)
                : item
        )));
    };

    const removeUnmatchedPositions = () => {
        setSignaturePositions(normalizeLocationSeq(signaturePositions.filter(item =>
            !isPositionForActiveFile(item) || isPositionAssignedToRecipient(item)
        )));
    };

    const updateSignatureType = (position: any, signatureTypeEnumId: string) => {
        updatePositionById(position.locationSeqId, item => ({
            ...item,
            signatureTypeEnumId,
            widthPx: 200,
            heightPx: signatureTypeEnumId === 'AgtImageVerified' ? 100 : 65
        }));
    };

    const updatePositionDimension = (position: any, field: 'widthPx' | 'heightPx', value: number) => {
        const canvas = pageCanvases[position.pageIndex];
        const maxValue = field === 'widthPx'
            ? (canvas ? canvas.width - (position.x || 0) : 1000)
            : (canvas ? canvas.height - (position.y || 0) : 1000);
        updatePositionById(position.locationSeqId, item => ({
            ...item,
            [field]: Math.min(Math.max(value, 0), maxValue)
        }));
    };

    const updatePositionFontSize = (position: any, value: number) => {
        updatePositionById(position.locationSeqId, item => ({
            ...item,
            fontSize: Math.max(value, 1)
        }));
    };

    const hasUnmatchedTemplatePositions = activeSignaturePositions.some(position => !isPositionAssignedToRecipient(position));

    return (
        <div className="flex flex-column lg:flex-row h-full gap-0 overflow-hidden" style={{ minHeight: '600px', background: 'var(--surface-ground)' }}>
            {/* Left Panel: Signer Selection and Position List */}
            <div className="col-12 lg:col-3 flex flex-column gap-3 p-3 overflow-y-auto border-right-1 surface-border shadow-2" style={{ maxHeight: 'calc(100vh - 120px)', background: 'var(--surface-section)' }}>
                <div className="flex flex-column gap-1 mb-3">
                    <label className="text-color text-xs font-bold opacity-60 uppercase mb-2">1. Chọn người ký</label>
                    <Dropdown
                        className="w-full"
                        style={{ background: 'var(--surface-section)', color: 'var(--text-color)' }}
                        optionLabel="label"
                        options={documentRecipients.map(r => ({
                            value: r._internalId,
                            _internalId: r._internalId,
                            partyId: r.party?.partyId || r.partyId,
                            roleTypeId: r.roleTypeId,
                            label: r.party?.label || r.partyName || 'Chưa chọn đối tượng'
                        }))}
                        value={selectedSigner?._internalId || null}
                        onChange={(e) => {
                            const opt = documentRecipients.map(r => ({
                                value: r._internalId,
                                _internalId: r._internalId,
                                partyId: r.party?.partyId || r.partyId,
                                roleTypeId: r.roleTypeId,
                                label: r.party?.label || r.partyName
                            })).find(o => o.value === e.value);
                            setSelectedSigner(opt as any);
                        }}
                        placeholder="Chọn người ký"
                        disabled={isViewMode}
                    />
                </div>

                {!isViewMode && selectedSigner && (
                    <div className="mb-4 surface-card p-3 border-round-lg border-1 surface-border">
                        <label className="text-color text-xs font-bold opacity-60 uppercase block mb-3">2. Công cụ thiết lập</label>
                        <Button
                            icon="pi pi-plus"
                            label="Thêm vị trí ký"
                            severity="danger"
                            className="w-full shadow-4 p-button-lg font-bold"
                            style={{ background: '#ef4444', border: 'none' }}
                            draggable
                            onDragStart={(event) => {
                                event.dataTransfer.setData('text/plain', 'electronic-signature');
                            }}
                        />
                        <div className="flex mt-3 align-items-center gap-2 text-color-secondary opacity-60">
                            <i className="pi pi-info-circle text-xs"></i>
                            <small className="italic text-xs">Kéo thả nút trên vào vùng cần ký</small>
                        </div>

                        <Divider className="surface-border my-3" />

                        <div className="flex flex-column gap-2">
                            <label className="text-color-secondary opacity-60" style={{ fontSize: '11px' }}>Loại chữ ký sẽ tạo</label>
                            <div className="flex flex-wrap gap-2">
                                <div
                                    className={`flex-1 min-w-min cursor-pointer border-round-md p-2 flex flex-column align-items-center gap-1 border-1 transition-all transition-duration-200 ${selectedSignatureType === 'AgtDigitalVerified' ? 'bg-primary-reverse shadow-2 border-primary' : 'bg-surface-100 border-surface-200 opacity-60'}`}
                                    onClick={() => setSelectedSignatureType('AgtDigitalVerified')}
                                >
                                    <i className={`pi pi-verified ${selectedSignatureType === 'AgtDigitalVerified' ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '18px' }}></i>
                                    <span className={`font-bold ${selectedSignatureType === 'AgtDigitalVerified' ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '10px' }}>KÝ SỐ</span>
                                </div>
                                <div
                                    className={`flex-1 min-w-min cursor-pointer border-round-md p-2 flex flex-column align-items-center gap-1 border-1 transition-all transition-duration-200 ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'bg-primary-reverse shadow-2 border-primary' : 'bg-surface-100 border-surface-200 opacity-60'}`}
                                    onClick={() => setSelectedSignatureType('AgtImageVerified')}
                                >
                                    <i className={`pi pi-image ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '18px' }}></i>
                                    <span className={`font-bold ${(selectedSignatureType === 'AgtImageVerified' || selectedSignatureType === 'AgtHandwritingVerified') ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '10px' }}>KÝ ẢNH</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Divider className="surface-border my-2" />


                {hasUnmatchedTemplatePositions && !isViewMode && (
                    <div className="surface-100 border-1 border-orange-300 border-round p-2 text-orange-700 text-xs flex flex-column gap-2">
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-exclamation-triangle mt-1" />
                            <span>Có vị trí ký từ mẫu chưa gắn với người ký hiện tại. Hãy gán lại hoặc xóa trước khi hoàn tất.</span>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Button label="Gán tất cả cho người đang chọn" size="small" text icon="pi pi-link" disabled={!selectedSigner} onClick={assignAllUnmatchedToSelectedSigner} />
                            <Button label="Xóa vị trí chưa gắn" size="small" text severity="danger" icon="pi pi-trash" onClick={removeUnmatchedPositions} />
                        </div>
                    </div>
                )}
                <div className="flex-1 flex flex-column gap-3">
                    <span className="text-color text-xs font-bold opacity-60 uppercase mb-2">
                        Vị trí ký hiện có ({activeSignaturePositions.length})
                    </span>
                    <div className="flex flex-column gap-3">
                        {activeSignaturePositions.map((position, index) => (
                            <div key={position.locationSeqId || index} className="flex flex-column gap-2 mb-3" style={isViewMode ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                                <div className="flex justify-content-between align-items-center mb-1">
                                    <b className="text-color" style={{ fontSize: '13px' }}>Vị trí {index + 1}: {(() => {
                                        const recipient = getRecipientForPosition(position);
                                        return getRecipientLabel(recipient, position.partyName || position.label);
                                    })()}</b>
                                    {!isViewMode && (
                                        <Button icon="pi pi-trash" text severity="danger" size="small" className="h-1.5rem w-1.5rem p-0" onClick={() => removeSignaturePosition(position.locationSeqId)} />
                                    )}
                                </div>
                                {!isPositionAssignedToRecipient(position) && !isViewMode && (
                                    <div className="border-1 border-orange-200 surface-100 text-orange-700 border-round p-2 text-xs flex flex-column gap-2">
                                        <span><i className="pi pi-exclamation-triangle mr-1" />Vị trí này chưa gắn với người ký hiện tại.</span>
                                        <div className="flex gap-2 flex-wrap">
                                            <Button label="Gán cho người đang chọn" size="small" text icon="pi pi-link" disabled={!selectedSigner} onClick={() => assignPositionToSelectedSigner(position)} />
                                            <Button label="Xóa" size="small" text severity="danger" icon="pi pi-trash" onClick={() => removeSignaturePosition(position.locationSeqId)} />
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-column col-12 p-0 mt-2">
                                        <label className="text-color-secondary mb-1" style={{ fontSize: '11px' }}>Loại chữ ký</label>
                                    <Dropdown
                                            value={getEffectiveSignatureType(position)}
                                            options={signatureTypeOptions}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => updateSignatureType(position, e.value)}
                                            disabled={isViewMode}
                                            className="p-inputtext-sm w-full"
                                            style={{ background: 'var(--surface-ground)', fontSize: '12px' }}
                                            itemTemplate={(option) => (
                                                <div className="flex align-items-center gap-2">
                                                    <i className={option.icon} style={{ fontSize: '12px' }}></i>
                                                    <span style={{ fontSize: '12px' }}>{option.label}</span>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary mb-1" style={{ fontSize: '11px' }}>Chiều rộng</label>
                                            <InputText type="number" value={position.widthPx || 200} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode}
                                                onChange={(e) => updatePositionDimension(position, 'widthPx', parseInt(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary mb-1" style={{ fontSize: '11px' }}>Chiều cao</label>
                                            <InputText type="number" value={position.heightPx || (getEffectiveSignatureType(position) === 'AgtImageVerified' ? 100 : 65)} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode}
                                                onChange={(e) => updatePositionDimension(position, 'heightPx', parseInt(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="flex flex-column col-4 p-0">
                                            <label className="text-color-secondary mb-1" style={{ fontSize: '11px' }}>Cỡ chữ</label>
                                            <InputText type="number" value={position.fontSize || 12} size="small" className="p-inputtext-sm w-full" style={{ background: 'var(--surface-ground)', fontSize: '11px' }} disabled={isViewMode}
                                                onChange={(e) => updatePositionFontSize(position, parseInt(e.target.value) || 0)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    {activeSignaturePositions.length === 0 && (
                        <div className="p-5 text-center text-color-secondary opacity-20 border-1 border-dashed surface-border border-round-lg flex flex-column align-items-center gap-2">
                            <i className="pi pi-map-marker text-2xl"></i>
                            <span className="text-xs">Chưa có vị trí ký nào được xác định</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Middle Panel: PDF Preview */}
            <div className="flex-1 flex flex-column relative overflow-hidden bg-surface-200">
                {enableZoom && !canvasLoading && pageCanvases.length > 0 && (
                    <div className="absolute top-0 left-0 w-full flex justify-content-center pointer-events-none" style={{ zIndex: 210, paddingTop: '0.75rem' }}>
                        <div className="flex align-items-center gap-1 bg-white border-1 border-gray-200 border-round-xl shadow-2 p-1 pointer-events-auto" style={{ minHeight: '2.5rem' }}>
                            <Button
                                icon="pi pi-search-minus"
                                className="p-button-rounded p-button-text p-button-sm"
                                aria-label="Thu nhỏ trang ký"
                                tooltip="Thu nhỏ"
                                tooltipOptions={{ position: 'bottom' }}
                                onClick={() => changeZoom(-25)}
                                disabled={zoomPercent <= 50}
                            />
                            <Button
                                label={`${zoomPercent}%`}
                                className="p-button-text p-button-sm"
                                style={{ minWidth: '4.5rem', fontVariantNumeric: 'tabular-nums' }}
                                aria-label={`Mức zoom trang ký ${zoomPercent}%, đặt lại 100%`}
                                tooltip="Đặt lại 100%"
                                tooltipOptions={{ position: 'bottom' }}
                                onClick={() => setZoomPercent(100)}
                            />
                            <Button
                                icon="pi pi-search-plus"
                                className="p-button-rounded p-button-text p-button-sm"
                                aria-label="Phóng to trang ký"
                                tooltip="Phóng to"
                                tooltipOptions={{ position: 'bottom' }}
                                onClick={() => changeZoom(25)}
                                disabled={zoomPercent >= 200}
                            />
                        </div>
                    </div>
                )}
                {canvasLoading && (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center bg-white-alpha-40" style={{ zIndex: 10 }}>
                        <i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                        <p className="mt-2 font-medium text-700">Đang xử lý PDF...</p>
                    </div>
                )}

                {!canvasLoading && pageCanvases.length > 0 && (
                    <ScrollPanel className="flex-1 w-full custom-scroll" style={{ height: '0' }}>
                        <div
                            className="px-3 flex flex-column min-h-full"
                            style={{
                                background: 'var(--surface-200)',
                                alignItems: zoomPercent <= 100 ? 'center' : 'flex-start',
                                paddingTop: enableZoom ? '4.5rem' : '1.25rem',
                                paddingBottom: '1.25rem'
                            }}
                        >
                            {pageCanvases.map((canvas, index) => (
                                <React.Fragment key={index}>
                                    <div
                                        ref={ref => {
                                            pageWrapperRefs.current[index] = ref;
                                        }}
                                        data-page-index={index}
                                        className="shadow-8 bg-white mb-2 relative"
                                        onDrop={(event) => handleDrop(event, index)}
                                        onDragOver={(event) => event.preventDefault()}
                                        style={{
                                            width: canvas?.width
                                                ? (enableZoom
                                                    ? `min(${canvas.width * zoomPercent / 100}px, ${zoomPercent}%)`
                                                    : `${canvas.width}px`)
                                                : 'fit-content',
                                            maxWidth: enableZoom ? 'none' : '100%'
                                        }}
                                    >
                                        <canvas ref={ref => {
                                            if (ref && canvas) {
                                                const context = ref.getContext('2d');
                                                if (context) {
                                                    ref.width = canvas.width;
                                                    ref.height = canvas.height;
                                                    context.drawImage(canvas, 0, 0);
                                                    canvasRefs.current[index] = ref;
                                                }
                                            }
                                        }} className="block" style={{ width: '100%', height: 'auto' }}></canvas>

                                        <div
                                            className="absolute top-0 left-0"
                                            style={{
                                                width: canvas?.width || '100%',
                                                height: canvas?.height || '100%',
                                                transform: `scale(${pageScales[index] || 1})`,
                                                transformOrigin: 'top left',
                                                zIndex: 100
                                            }}
                                        >
                                            {signaturePositions
                                            .filter(element => Number(element.pageIndex) === index && isPositionForActiveFile(element) && isPositionAssignedToRecipient(element))
                                            .map((element) => {
                                                const nodeRef = draggableRefs.current[element.locationSeqId] || (draggableRefs.current[element.locationSeqId] = React.createRef());
                                                const pageScale = pageScales[index] || 1;
                                                const signatureType = getEffectiveSignatureType(element);
                                                const signatureWidth = element.widthPx || 200;
                                                const signatureHeight = getEffectiveSignatureHeight(element, signatureWidth, signatureType);
                                                return (
                                                    <Draggable
                                                        nodeRef={nodeRef}
                                                        key={element.locationSeqId}
                                                        position={{
                                                            x: Number(element.x) || 0,
                                                            y: Number(element.y) || 0
                                                        }}
                                                        scale={pageScale}
                                                        bounds="parent"
                                                        disabled={isViewMode}
                                                        onStop={(event, data) => handleDragStop(event, data, element.locationSeqId)}
                                                    >
                                                        <div
                                                            ref={nodeRef}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                background: 'rgba(255,255,255, 0.9)',
                                                                boxSizing: 'border-box',
                                                                padding: '5px',
                                                                borderRadius: '5px',
                                                                cursor: isViewMode ? 'default' : 'move',
                                                                borderColor: isPositionAssignedToRecipient(element) ? 'red' : '#f59e0b',
                                                                borderWidth: '1px',
                                                                borderStyle: 'dashed',
                                                                height: signatureHeight,
                                                                width: signatureWidth,
                                                                minWidth: signatureWidth,
                                                                maxWidth: signatureWidth,
                                                                minHeight: signatureHeight,
                                                                overflow: 'visible',
                                                                zIndex: 100
                                                            }}
                                                            className={'flex'}
                                                        >
                                                            <div className={'col-11 w-full flex flex-column relative'} style={{ fontSize: element.fontSize || 12, padding: 0, color: '#000', lineHeight: 1.2, overflow: 'visible' }}>
                                                                <div className="absolute top-0 right-0 p-1 opacity-20">
                                                                    <i className={`pi ${signatureType === 'AgtImageVerified' ? 'pi-image' : 'pi-verified'}`} style={{ fontSize: '1.2rem' }}></i>
                                                                </div>
                                                                <div className="flex-1 flex flex-column justify-content-center align-items-center text-center px-1">
                                                                    {(() => {
                                                                        const label = getSignatureDisplayLabel(element);

                                                                        if (signatureType === 'AgtImageVerified') {
                                                                            return (
                                                                                <span
                                                                                    className="font-bold border-bottom-1 surface-border pb-1 mb-1 block w-full opacity-80"
                                                                                    style={{
                                                                                        fontSize: '0.9em',
                                                                                        whiteSpace: 'nowrap',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis'
                                                                                    }}
                                                                                    title={label}
                                                                                >
                                                                                    {label}
                                                                                </span>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <div className="text-left w-full" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                                                <span className="font-bold">[KÝ SỐ]</span><br />
                                                                                - Ký bởi: {label}<br />
                                                                                - Ngày ký: dd/MM/yyyy
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                            {!isViewMode && (
                                                                <i
                                                                    className="pi pi-times-circle absolute top-0 right-0 cursor-pointer bg-white border-circle text-red-500"
                                                                    style={{ transform: 'translate(50%, -50%)', fontSize: '14px', zIndex: 101 }}
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        removeSignaturePosition(element.locationSeqId);
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </Draggable>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {/* Page Number Indicator between pages */}
                                    <div className="text-600 font-bold text-lg my-4 bg-white border-circle w-3rem h-3rem flex align-items-center justify-content-center shadow-4 border-1 border-300">
                                        {index + 1}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </ScrollPanel>
                )}

                {!canvasLoading && pageCanvases.length === 0 && (
                    <div className="flex-1 flex flex-column align-items-center justify-content-center text-500 bg-gray-50">
                        <i className="pi pi-file-pdf text-6xl mb-3 opacity-20"></i>
                        <span className="font-medium text-lg">Vui lòng quay lại bước 1 để tải tệp tài liệu chính</span>
                    </div>
                )}
            </div>

            {/* Right Panel: Files Accordion & Thumbnails */}
            <div
                className="hidden lg:flex col-12 lg:col-3 flex-column gap-0 overflow-hidden border-left-1 surface-border shadow-2"
                style={{ height: '100%', minHeight: 0, maxHeight: '100%', background: 'var(--surface-section)' }}
            >
                <div className="flex align-items-center justify-content-between px-3 py-2 surface-card border-bottom-1 surface-border">
                    <span className="text-sm font-bold text-color uppercase">Tài liệu</span>
                    {pageCanvases.length > 0 && (
                        <span className="text-sm text-color">
                            <b className="text-primary">{activePageIndex + 1}</b> / {pageCanvases.length}
                        </span>
                    )}
                </div>

                {/* Download All Banner */}
                {/* Vertical File List (Accordion) */}
                <ScrollPanel className="flex-1 w-full custom-scroll" style={{ height: '0', minHeight: 0 }}>
                    <div className="p-2 flex flex-column gap-1 bg-surface-ground">
                        {mainFiles.map((file, idx) => {
                            const isActive = activeMainFileIndex === idx;
                            return (
                                <div key={idx} className="flex flex-column surface-card border-bottom-1 surface-border overflow-hidden shadow-1">
                                {/* File Header */}
                                <div
                                    onClick={() => onSetActiveFile(idx)}
                                    className={`flex align-items-center justify-content-between p-3 cursor-pointer transition-all hover:bg-black-alpha-5 ${isActive ? 'bg-primary-50' : ''}`}
                                >
                                    <div className="flex align-items-start gap-2 flex-1 overflow-hidden">
                                        <i className={`pi pi-file-pdf mt-1 ${isActive ? 'text-primary' : 'text-color-secondary'}`} style={{ fontSize: '1.2rem' }}></i>
                                        <span className={`text-sm font-medium line-height-3 ${isActive ? 'text-primary' : 'text-color'}`} style={{ wordBreak: 'break-all' }}>
                                            {file.name}
                                        </span>
                                    </div>
                                    <div className="flex align-items-center gap-3 flex-shrink-0 ml-2">
                                        <i className={`pi pi-chevron-${isActive ? 'up' : 'down'} text-xs text-color-secondary`}></i>
                                    </div>
                                </div>

                                {/* File Content (Thumbnails) - Only visible when active */}
                                {isActive && (
                                    <div className="p-3 bg-white border-top-1 surface-border">
                                        {canvasLoading ? (
                                            <div className="py-4 text-center">
                                                <i className="pi pi-spin pi-spinner text-primary text-xl"></i>
                                            </div>
                                        ) : (
                                            <div className="flex flex-column">
                                                <div className="flex flex-column align-items-center gap-4">
                                                    {pageCanvases.map((canvas, index) => {
                                                        const isActivePage = activePageIndex === index;
                                                        const signatureCount = signatureCountByPage[index] || 0;

                                                        return (
                                                            <button
                                                                key={index}
                                                                type="button"
                                                                aria-label={`Đi tới trang ${index + 1}${signatureCount ? `, có ${signatureCount} vị trí ký` : ''}`}
                                                                aria-current={isActivePage ? 'page' : undefined}
                                                                className={`appearance-none border-none bg-transparent p-2 border-round cursor-pointer flex flex-column align-items-center gap-2 transition-all transition-duration-200 ${isActivePage ? 'surface-100 shadow-2' : ''}`}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setActivePageIndex(index);
                                                                    pageWrapperRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                }}
                                                            >
                                                                <div
                                                                    className={`relative border-round-sm overflow-visible transition-all hover:shadow-6 ${isActivePage ? 'border-3 border-primary shadow-4' : 'border-1 surface-border shadow-2'}`}
                                                                    style={{ width: '120px' }}
                                                                >
                                                                    <img
                                                                        src={canvas.toDataURL()}
                                                                        alt={`Trang ${index + 1}`}
                                                                        className="w-full h-auto block border-round-sm"
                                                                    />
                                                                    {signatureCount > 0 && (
                                                                        <span
                                                                            className="absolute flex align-items-center gap-1 bg-orange-500 text-white border-circle px-2 py-1 shadow-3"
                                                                            style={{ top: '-0.65rem', right: '-0.8rem', fontSize: '0.7rem', fontWeight: 700 }}
                                                                            title={`${signatureCount} vị trí ký trên trang này`}
                                                                        >
                                                                            <i className="pi pi-pencil" style={{ fontSize: '0.65rem' }} />
                                                                            {signatureCount}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className={`text-xs font-bold ${isActivePage ? 'text-primary' : 'text-color-secondary'}`}>
                                                                    Trang {index + 1}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                </div>
                            );
                        })}
                    </div>
                </ScrollPanel>
            </div>


        </div>
    );
};
